// The panel's discovery hook: `appId` and the scheduled jobs of the NAMED selected version, read once.
// Zero jobs and several jobs are both real answers and never choose silently: `job` is set only when the
// App schedules exactly one job, or when the person picked one of several; `needsChoice` says a pick is due.
import { useCallback, useEffect, useState } from 'react';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import {
  createControlSelectionApi,
  type ControlSelection,
  type ScheduledJob,
} from '../control-selection-api';

export function useControlSelection(
  artifactId: string,
  versionId: string,
  explicit?: { read(): Promise<ControlSelection> },
) {
  const bound = useSessionBoundFetch();
  const [own] = useState(() => createControlSelectionApi(artifactId, versionId, bound));
  const api = explicit ?? own;
  const [selection, setSelection] = useState<ControlSelection | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    try {
      const next = await api.read();
      setSelection(next);
      setUnavailable(false);
      // A pick that is no longer a scheduled job of the current declaration is dropped.
      setPicked((current) =>
        current && next.jobs.some((job) => job.id === current) ? current : null,
      );
    } catch {
      setUnavailable(true);
    }
  }, [api]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const jobs: ScheduledJob[] = selection?.jobs ?? [];
  const job = jobs.length === 1 ? jobs[0] : (jobs.find((entry) => entry.id === picked) ?? null);
  return {
    selection,
    appId: selection?.appId ?? null,
    jobs,
    job,
    none: selection !== null && jobs.length === 0,
    needsChoice: jobs.length > 1 && job === null,
    unavailable,
    pick: (id: string) => setPicked(jobs.some((entry) => entry.id === id) ? id : null),
    refresh,
  };
}
